<template>
  <div class="tradingview-widget-container">
    <div
      ref="widgetContainer"
      class="tradingview-widget-container__widget"
    ></div>
  </div>
</template>

<script>
import { propValidator } from '@fmpedia/validators'
import { PROP_TYPES } from '@/utils/validators'
import { COOKIE_ENTITY_TYPE } from 'enums/oneTrust'
import { generateIlUrl } from '@/utils/helpers/url-generators'
import { mapActions } from 'vuex'
import { createOneTrustHandlerInstance } from '@/utils/enums/oneTrust/one-trust-handler'
import {
  TV_SYMBOL,
  TV_SYMBOL_NAME_BY_TV_SYMBOL
} from 'enums/live-widgets/symbols'

const DEFAULT_CONFIG = {
  symbols: [
    {
      proName: TV_SYMBOL.FOREXCOM_SPX500,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.FOREXCOM_SPX500]
    },
    {
      proName: TV_SYMBOL.NASDAQ_IXIC,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.NASDAQ_IXIC]
    },
    {
      proName: TV_SYMBOL.PEPPERSTONE_VIX,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.PEPPERSTONE_VIX]
    },
    {
      proName: TV_SYMBOL.XETR_DAX,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.XETR_DAX]
    },
    {
      proName: TV_SYMBOL.HSI_HSI,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.HSI_HSI]
    },
    {
      proName: TV_SYMBOL.FX_EUR_USD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.FX_EUR_USD]
    },
    {
      proName: TV_SYMBOL.FX_USD_JPY,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.FX_USD_JPY]
    },
    {
      proName: TV_SYMBOL.FX_GBP_USD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.FX_GBP_USD]
    },
    {
      proName: TV_SYMBOL.FX_AUD_USD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.FX_AUD_USD]
    },
    {
      proName: TV_SYMBOL.BITSTAMP_BTC_USD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.BITSTAMP_BTC_USD]
    },
    {
      proName: TV_SYMBOL.BITSTAMP_ETH_USD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.BITSTAMP_ETH_USD]
    },
    {
      proName: TV_SYMBOL.VANTAGE_CHINA50,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.VANTAGE_CHINA50]
    },
    {
      proName: TV_SYMBOL.INDEX_SX5E,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.INDEX_SX5E]
    },
    {
      proName: TV_SYMBOL.JSE_J200,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.JSE_J200]
    },
    {
      proName: TV_SYMBOL.OANDA_XAUUSD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.OANDA_XAUUSD]
    },
    {
      proName: TV_SYMBOL.OANDA_WTICOUSD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.OANDA_WTICOUSD]
    },
    {
      proName: TV_SYMBOL.OANDA_XCUUSD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.OANDA_XCUUSD]
    },
    {
      proName: TV_SYMBOL.OANDA_NATGASUSD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.OANDA_NATGASUSD]
    },
    {
      proName: TV_SYMBOL.BINANCE_ETHUSDT,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.BINANCE_ETHUSDT]
    },
    {
      proName: TV_SYMBOL.BINANCE_BTCUSD,
      title: TV_SYMBOL_NAME_BY_TV_SYMBOL[TV_SYMBOL.BINANCE_BTCUSD]
    }
  ],
  showSymbolLogo: true,
  isTransparent: false,
  displayMode: 'regular',
  colorTheme: 'light',
  locale: 'en'
}

export default {
  name: 'ATickerWidget',
  props: {
    symbols: propValidator([PROP_TYPES.ARRAY], false, () => [
      ...DEFAULT_CONFIG.symbols
    ]),
    showSymbolLogo: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      DEFAULT_CONFIG.showSymbolLogo
    ),
    isTransparent: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      DEFAULT_CONFIG.isTransparent
    ),
    displayMode: propValidator(
      [PROP_TYPES.STRING],
      false,
      DEFAULT_CONFIG.displayMode
    ),
    colorTheme: propValidator(
      [PROP_TYPES.STRING],
      false,
      DEFAULT_CONFIG.colorTheme
    )
  },
  methods: {
    ...mapActions({
      registerHandlerThatRequiresConsent:
        'one-trust/registerHandlerThatRequiresConsent'
    }),
    loadTradingViewWidget() {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src =
        'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js'

      const config = {
        symbols: this.symbols,
        showSymbolLogo: this.showSymbolLogo,
        isTransparent: this.isTransparent,
        displayMode: this.displayMode,
        colorTheme: this.colorTheme,
        largeChartUrl: generateIlUrl('/LiveCharts', this),
        customer: 'financemagnates'
      }

      script.innerHTML = JSON.stringify(config)
      this.$refs.widgetContainer.appendChild(script)
    }
  },
  mounted() {
    this.registerHandlerThatRequiresConsent(
      createOneTrustHandlerInstance({
        handler: this.loadTradingViewWidget,
        entityType: COOKIE_ENTITY_TYPE.TW_TICKER
      })
    )
  }
}
</script>

<style lang="scss" scoped>
.tradingview-widget-container {
  height: $tw-ticker-height;
  position: relative;
  top: 0;
  margin: 0 auto;
  transition: $header-transition;
  pointer-events: all;
}
</style>

<style lang="scss">
.no-tw-ticker {
  .tradingview-widget-container {
    pointer-events: none;
  }
}
</style>
